import styled from 'styled-components'
import {
  PiCellSignalFull,
  PiCellSignalHigh,
  PiCellSignalMedium,
  PiCellSignalLow,
} from 'react-icons/pi'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

type TrafficKey = 'very high' | 'high' | 'average' | 'low'

// DS2026 badge "Icon" tier (one step brighter than the "Text" tier): teal-600,
// green-600, amber-600, red-600.
const DS2026_STYLES: Record<
  TrafficKey,
  { color: string; Icon: React.ComponentType<{ size?: number }> }
> = {
  'very high': { color: '#0d9488', Icon: PiCellSignalFull },
  high: { color: '#16a34a', Icon: PiCellSignalHigh },
  average: { color: '#d97706', Icon: PiCellSignalMedium },
  low: { color: '#dc2626', Icon: PiCellSignalLow },
}

export function PredictedTrafficBadge({
  value,
  className,
  size = 'default',
}: {
  value: string | null
  className?: string
  size?: 'default' | 'small'
}) {
  const variant = useTableLinkVariant()

  if (variant === 'plain') {
    const key = value?.toLowerCase() as TrafficKey | undefined
    const style = key && DS2026_STYLES[key]
    if (!style) {
      return (
        <div className="flex">
          <span className="text-xs font-medium text-gray-400">
            {value ?? 'Unavailable'}
          </span>
        </div>
      )
    }
    const { color, Icon } = style
    return (
      <div className="flex">
        <span
          className={`inline-flex items-center gap-1 rounded-2xl bg-white py-1 pl-2 pr-3 text-sm font-medium leading-5 ${className ?? ''}`}
          style={{ color, letterSpacing: '-0.2px' }}
        >
          <Icon size={16} />
          {value}
        </span>
      </div>
    )
  }

  const getColorProps = (value: string | null) => {
    const value_lower = value?.toLowerCase() || 'unavailable'
    if (value_lower === 'very high')
      return { color: '#00876C', backgroundColor: '#e6f3f1' }
    if (value_lower === 'high')
      return { color: '#64901B', backgroundColor: '#eff5e4' }
    if (value_lower === 'average')
      return { color: '#E08B20', backgroundColor: '#fef6ec' }
    if (value_lower === 'low')
      return { color: '#D43D51', backgroundColor: '#fbecee' }
    return { color: '#959595' }
  }

  return (
    <div className="flex">
      <Badge {...getColorProps(value)} className={className} size={size}>
        {value ?? 'Unavailable'}
      </Badge>
    </div>
  )
}

const Badge = styled.div<{
  color?: string
  backgroundColor?: string
  size?: 'default' | 'small'
}>`
  border-radius: 10px;
  font-weight: 500;
  ${({ color }) => color && `color: ${color};`}
  ${({ backgroundColor }) =>
    backgroundColor && `background-color: ${backgroundColor};`}

  ${({ size }) =>
    size === 'small'
      ? `
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
      `
      : `
        font-size: 85%;
        padding: 2px 8px;
      `}
`
